import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  Package, 
  FileText,
  Menu,
  X,
  Droplets,
  Truck,
  Users,
  Box,
  ChevronDown,
  Database
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import InstallPWA, { InstallAppButton } from "@/components/InstallPWA";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/entrate", icon: ArrowDownToLine, label: "Entrate" },
  { path: "/uscite", icon: ArrowUpFromLine, label: "Uscite" },
  { path: "/partite", icon: Package, label: "Partite" },
  { path: "/report", icon: FileText, label: "Report" },
  { path: "/backup", icon: Database, label: "Backup" },
];

const archivioItems = [
  { path: "/archivio/fornitori", icon: Truck, label: "Fornitori" },
  { path: "/archivio/clienti", icon: Users, label: "Clienti" },
  { path: "/archivio/prodotti", icon: Box, label: "Prodotti" },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [archivioOpen, setArchivioOpen] = useState(false);
  const location = useLocation();

  const isArchivioActive = location.pathname.startsWith("/archivio");

  const getCurrentPageTitle = () => {
    const allItems = [...navItems, ...archivioItems];
    const current = allItems.find(item => item.path === location.pathname);
    return current?.label || "AP Depuratore";
  };

  return (
    <div className="min-h-screen w-full bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white h-14 flex items-center px-4 shadow-lg">
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-800 transition-colors"
        >
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <div className="flex items-center gap-2 ml-2">
          <div className="w-7 h-7 bg-orange-500 rounded-md flex items-center justify-center">
            <Droplets className="w-4 h-4 text-white" />
          </div>
          <span className="font-heading font-bold text-sm">{getCurrentPageTitle()}</span>
        </div>
      </header>

      {/* Desktop Grid Layout */}
      <div className="hidden lg:grid lg:grid-cols-[260px_1fr] min-h-screen">
        {/* Sidebar - Desktop */}
        <aside className="h-screen sticky top-0 bg-gradient-to-b from-slate-900 to-slate-800 text-white flex flex-col">
          {/* Logo */}
          <div className="p-5 border-b border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
                <Droplets className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-base tracking-tight text-white">AP DEPURATORE</h1>
                <p className="text-[11px] text-slate-400">Sistema di Gestione</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all font-medium text-sm",
                    isActive
                      ? "bg-orange-500 text-white shadow-md shadow-orange-500/20"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {/* Archivio Section */}
            <Collapsible open={archivioOpen || isArchivioActive} onOpenChange={setArchivioOpen}>
              <CollapsibleTrigger asChild>
                <button
                  data-testid="nav-archivio"
                  className={cn(
                    "flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all font-medium text-sm",
                    isArchivioActive
                      ? "bg-white/10 text-white"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Package size={18} />
                    <span>Archivi</span>
                  </div>
                  <ChevronDown 
                    size={16} 
                    className={cn(
                      "transition-transform duration-200",
                      (archivioOpen || isArchivioActive) && "rotate-180"
                    )} 
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-3 space-y-0.5 mt-1">
                {archivioItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-archivio-${item.label.toLowerCase()}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg transition-all font-medium text-sm",
                        isActive
                          ? "bg-orange-500 text-white shadow-md shadow-orange-500/20"
                          : "text-slate-400 hover:bg-white/5 hover:text-white"
                      )}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>

            {/* Install App Button */}
            <div className="mt-3 pt-3 border-t border-slate-700/50">
              <InstallAppButton />
            </div>
          </nav>

          {/* Footer */}
          <div className="p-3 border-t border-slate-700/50">
            <p className="text-[10px] text-slate-500 text-center">
              v1.0.0 - Centro Depurazione
            </p>
          </div>
        </aside>

        {/* Main Content - Desktop */}
        <main className="w-full min-w-0 p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden pt-14 min-h-screen">
        {/* Mobile Sidebar */}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 to-slate-800 text-white flex flex-col transform transition-transform duration-300 ease-out",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {/* Close button area */}
          <div className="h-14" />

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-lg transition-all font-medium text-sm",
                    isActive
                      ? "bg-orange-500 text-white"
                      : "text-slate-300 hover:bg-white/5"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {/* Mobile Archivio */}
            <Collapsible open={archivioOpen || isArchivioActive} onOpenChange={setArchivioOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex items-center justify-between w-full px-3 py-3 rounded-lg transition-all font-medium text-sm",
                    isArchivioActive ? "bg-white/10 text-white" : "text-slate-300"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Package size={20} />
                    <span>Archivi</span>
                  </div>
                  <ChevronDown 
                    size={16} 
                    className={cn(
                      "transition-transform duration-200",
                      (archivioOpen || isArchivioActive) && "rotate-180"
                    )} 
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-1 mt-1">
                {archivioItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm",
                        isActive ? "bg-orange-500 text-white" : "text-slate-400"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>

            {/* Mobile Install */}
            <div className="mt-3 pt-3 border-t border-slate-700/50">
              <InstallAppButton />
            </div>
          </nav>
        </aside>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-30 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Main Content */}
        <main className="w-full p-4">
          <Outlet />
        </main>
      </div>

      {/* PWA Install Banner */}
      <InstallPWA />
    </div>
  );
}
